/**
 * Wrapper around the face-api.js global. Loads models lazily from CDN.
 * Used by the HR Reloj Checador kiosk and the employee enrolment screen.
 *
 * IMPORTANT: face-api.js exposes itself as a global `faceapi`. We load
 * the minified script in index.html and only fetch the model weights here.
 */
const MODELS_URI = "https://justadudewhohacks.github.io/face-api.js/models";

let modelsLoaded = false;
let loadingPromise = null;

function ensureFaceapi() {
  return new Promise((resolve, reject) => {
    if (window.faceapi) return resolve(window.faceapi);
    let tries = 0;
    const interval = setInterval(() => {
      tries += 1;
      if (window.faceapi) { clearInterval(interval); resolve(window.faceapi); }
      else if (tries > 50) { clearInterval(interval); reject(new Error("face-api.js no se pudo cargar")); }
    }, 100);
  });
}

export async function loadFaceModels() {
  if (modelsLoaded) return window.faceapi;
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    const faceapi = await ensureFaceapi();
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URI),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODELS_URI),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URI),
    ]);
    modelsLoaded = true;
    return faceapi;
  })();
  return loadingPromise;
}

/**
 * Detect a single face in a <video> or <canvas> element and return the
 * 128-dim descriptor. Returns null when no face (or several) are detected.
 */
export async function detectFaceDescriptor(input) {
  const faceapi = await loadFaceModels();
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });
  const result = await faceapi
    .detectSingleFace(input, opts)
    .withFaceLandmarks(true)
    .withFaceDescriptor();
  if (!result) return null;
  return Array.from(result.descriptor);
}

/**
 * Find the employee whose stored face descriptor is closest to the given one.
 * Returns { employee, distance } or null if nobody is below the threshold.
 */
export function matchDescriptor(targetDescriptor, employees, threshold = 0.55) {
  if (!targetDescriptor || !Array.isArray(employees)) return null;
  let best = null;
  for (const emp of employees) {
    const desc = emp.face_descriptor;
    if (!Array.isArray(desc) || desc.length !== 128) continue;
    let sum = 0;
    for (let i = 0; i < 128; i += 1) {
      const d = targetDescriptor[i] - desc[i];
      sum += d * d;
    }
    const dist = Math.sqrt(sum);
    if (best === null || dist < best.distance) {
      best = { employee: emp, distance: dist };
    }
  }
  if (!best || best.distance > threshold) return null;
  return best;
}

/**
 * Snapshot the current <video> frame to a data URL (JPEG quality 0.7).
 */
export function snapshotVideo(videoEl, scale = 0.5) {
  if (!videoEl) return null;
  const w = Math.floor((videoEl.videoWidth || 320) * scale);
  const h = Math.floor((videoEl.videoHeight || 240) * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(videoEl, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.7);
}
